import { useState } from 'react';
import { toast } from 'sonner';
import { publicApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { NEWSLETTER } from '@/constants/testIds';

export const NewsletterForm = ({ variant = 'inline', className = '' }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [consent, setConsent] = useState(true);
  const [hp, setHp] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // { ok, message }

  const submit = async (e) => {
    e.preventDefault();
    setStatus(null);
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus({ ok: false, message: 'Please enter a valid email address.' });
      return;
    }
    if (!consent) {
      setStatus({ ok: false, message: 'Please agree to receive The Field Report.' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await publicApi.subscribe({ email, first_name: firstName, consent, honeypot: hp });
      setStatus({ ok: true, message: res.message || "You're in." });
      toast.success(res.message || "You're in.");
      setEmail('');
      setFirstName('');
    } catch (err) {
      const message = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setStatus({ ok: false, message });
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const compact = variant === 'inline';

  return (
    <form onSubmit={submit} className={`w-full ${className}`} noValidate>
      <input
        type="text"
        name="company"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />
      <div className={compact ? 'flex flex-col gap-3 sm:flex-row sm:items-end' : 'grid gap-4 sm:grid-cols-2'}>
        {!compact && (
          <div>
            <Label htmlFor="nl-first-name" className="kicker mb-2 block">First name (optional)</Label>
            <Input
              id="nl-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              data-testid={NEWSLETTER.firstNameInput}
              placeholder="Your name"
            />
          </div>
        )}
        <div className={compact ? 'flex-1' : ''}>
          <Label htmlFor="nl-email" className="kicker mb-2 block">Email</Label>
          <Input
            id="nl-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid={NEWSLETTER.emailInput}
            placeholder="you@example.com"
            required
          />
        </div>
        <Button
          type="submit"
          disabled={submitting}
          data-testid={NEWSLETTER.submitButton}
          className="h-10 min-w-[10rem]"
        >
          {submitting ? 'Subscribing…' : 'Get The Field Report'}
        </Button>
      </div>

      {!compact && (
        <label className="mt-4 flex items-start gap-2 text-sm text-[hsl(var(--muted-foreground))]">
          <Checkbox
            checked={consent}
            onCheckedChange={(v) => setConsent(Boolean(v))}
            data-testid={NEWSLETTER.consentCheckbox}
            id="nl-consent"
          />
          <span>
            I agree to receive The Field Report by email. You can unsubscribe any time. No daily spam. No manufactured drama.
          </span>
        </label>
      )}

      {status && status.ok && (
        <p
          data-testid={NEWSLETTER.successMessage}
          className="mt-3 text-sm text-[hsl(var(--primary))]"
          role="status"
          aria-live="polite"
        >
          {status.message}
        </p>
      )}
      {status && !status.ok && (
        <p
          data-testid={NEWSLETTER.errorMessage}
          className="mt-3 text-sm text-[hsl(var(--destructive))]"
          role="alert"
        >
          {status.message}
        </p>
      )}
    </form>
  );
};
